import { Button, Spinner } from '@mgh-app/component-library';
import {
  LightingBrightIcon,
  LightingLowIcon,
  LightingMediumIcon,
  LightingPresetIcon,
} from 'components/Icons';
import { NewPresetButton } from 'components/NewPresetButton';
import { RoomButton } from 'components/RoomButton';
import { MainLayoutContext } from 'layouts/MainLayout';
import { Light, Thing, ThingIdentifier, ThingType } from 'models/Thing';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { selectRoomById } from 'services/RoomService';
import {
  selectThing,
  useDeletePresetMutation,
  useGetAllLightingValuesQuery,
  useGetLightingPresetsQuery,
  useGetThingsListQuery,
  useSetLightingPresetMutation,
  useSetLightValueMutation,
} from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  display,
  gap,
  gridAutoFlow,
  gridAutoRows,
  gridTemplateColumns,
  gridTemplateRows,
  justifyContent,
  justifyItems,
  justifySelf,
  margin,
  overflow,
  padding,
  tailwindClassNameConvertNegative,
  twCls,
  width,
} from 'style';
import { useLongPress } from '../../hooks/useLongPress';

import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple';

const isLighting = (thing: Thing) =>
  thing.identifier === ThingIdentifier.DMX && thing.thingType === ThingType.Individual;

const calculatePowerStatus = (things: Thing[], lightingsValues: Light[]) => {
  const result = things.filter(isLighting).reduce(
    (acc, thing) => ({
      power:
        (lightingsValues.find((v) => v.lightIds[0] === thing.id)?.power || 'OFF') === 'ON'
          ? true
          : acc.power,
    }),
    { power: false },
  );

  return result.power;
};

const getLightingIds = (things: Thing[]) =>
  things && things.filter(isLighting).map((thing) => thing.id);

export const MainLights = (): JSX.Element => {
  const { setTitle, showNavigationButtons, navigateBackAction } = useContext(MainLayoutContext);
  const { groupId = '', roomId = '' } = useParams();
  const { navigateAddPreset, navigateEditPreset, navigateRoom, navigateBack } = useAppNavigation();
  const currentPresetRef = useRef<string>('');
  const [allLightsOn, setAllLightsOn] = useState<boolean>();
  const [deletePreset] = useDeletePresetMutation();
  const {
    data: presets,
    isLoading: isLoadingPresets,
    refetch: refetchPresets,
  } = useGetLightingPresetsQuery(groupId, { refetchOnMountOrArgChange: true });
  const rippleRefs = useRef<Record<string, TouchRippleActions | null>>({});

  const showActions = async () => {
    await new Promise<void>((resolve) => {
      rippleRefs.current[currentPresetRef.current]?.stop(undefined, () => resolve());
    });

    const result = await ActionSheet.showActions({
      title: currentPresetRef.current,
      options: [
        {
          title: 'Edit Preset',
        },
        {
          title: 'Delete Preset',
          style: ActionSheetButtonStyle.Destructive,
        },
        {
          title: 'Cancel',
        },
      ],
    });

    switch (result.index) {
      case 0:
        navigateEditPreset(roomId, groupId, currentPresetRef.current);
        break;
      case 1:
        await deletePreset({ groupId, name: currentPresetRef.current });
        await refetchPresets();
        break;
      default:
        return;
    }
  };

  const longPressEvent = useLongPress(() => showActions(), {
    isPreventDefault: false,
    delay: 300,
  });

  const room = useAppSelector((state) => selectRoomById(state, roomId));
  const thing = useAppSelector((state) => selectThing(state, roomId, groupId));

  const { data: things, isLoading: isLoadingThings } = useGetThingsListQuery(roomId, {
    refetchOnMountOrArgChange: true,
  });
  const [setLightValue] = useSetLightValueMutation();
  const [setPreset] = useSetLightingPresetMutation();
  const {
    data: lightingsValues,
    isLoading: isLoadingLightingValues,
    refetch: refetchAllLightingValues,
  } = useGetAllLightingValuesQuery(undefined, { refetchOnMountOrArgChange: true });

  const isLoading = isLoadingPresets || isLoadingThings || isLoadingLightingValues;

  const isAnyLightingOn =
    things && lightingsValues && calculatePowerStatus(things, lightingsValues);

  const navigateBackToRoom = useCallback(() => {
    navigateRoom(roomId);
  }, [navigateRoom, roomId]);

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);
    navigateBackAction(() => navigateBackToRoom);

    return () => navigateBackAction(() => navigateBack);
  }, [
    navigateBackAction,
    navigateBack,
    room,
    room?.nickName,
    setTitle,
    showNavigationButtons,
    navigateBackToRoom,
  ]);

  useEffect(() => {
    setAllLightsOn(isAnyLightingOn);
  }, [isAnyLightingOn]);

  useEffect(() => {
    if (presets) {
      rippleRefs.current = Object.fromEntries(presets.map((preset) => [preset.name, null]));
    }
  }, [presets]);

  const toggleLights = async () => {
    if (things) {
      await setLightValue({
        lightIds: getLightingIds(things),
        power: allLightsOn ? 'OFF' : 'ON',
      }).unwrap();
      await refetchAllLightingValues().unwrap();
      setAllLightsOn(!allLightsOn);
    }
  };

  const createNewPreset = () => navigateAddPreset(roomId, groupId);

  return (
    <div
      className={twCls(
        margin(
          'mt-6',
          tailwindClassNameConvertNegative('-ml-2'),
          tailwindClassNameConvertNegative('-mr-2'),
        ),
        display('grid'),
        gridTemplateColumns('grid-cols-1'),
        gridTemplateRows('grid-rows-min-fc-min'),
        justifyContent('justify-center'),
        justifyItems('justify-items-center'),
      )}
    >
      <h2>{thing?.name}</h2>
      <div
        className={twCls(
          width('w-full'),
          margin(
            'mt-6',
            tailwindClassNameConvertNegative('-ml-2'),
            tailwindClassNameConvertNegative('-mr-2'),
          ),
          padding('pb-6'),
          overflow('overflow-auto'),
          display('grid'),
          gap('gap-2'),
          justifyContent('justify-center'),
          gridAutoFlow('grid-flow-row'),
          gridAutoRows('auto-rows-min'),
          gridTemplateColumns('grid-cols-responsive-buttons'),
        )}
      >
        {isLoading && <Spinner />}
        {!isLoading && (
          <>
            <RoomButton
              name="Bright"
              icon={<LightingBrightIcon />}
              onClick={() =>
                things &&
                setLightValue({ lightIds: getLightingIds(things), brightness: 80, power: 'ON' })
              }
            />
            <RoomButton
              name="Medium"
              icon={<LightingMediumIcon />}
              onClick={() =>
                things &&
                setLightValue({ lightIds: getLightingIds(things), brightness: 50, power: 'ON' })
              }
            />
            <RoomButton
              name="Low"
              icon={<LightingLowIcon />}
              onClick={() =>
                things &&
                setLightValue({ lightIds: getLightingIds(things), brightness: 30, power: 'ON' })
              }
            />
            {presets &&
              presets.map((preset) => (
                <RoomButton
                  touchRippleRef={(rippleAction) =>
                    (rippleRefs.current[preset.name] = rippleAction)
                  }
                  key={preset.name}
                  name={preset.name}
                  icon={<LightingPresetIcon />}
                  {...longPressEvent}
                  onMouseDown={(ev) => {
                    currentPresetRef.current = preset.name;
                    longPressEvent.onMouseDown(ev);
                  }}
                  onTouchStart={(ev) => {
                    currentPresetRef.current = preset.name;
                    longPressEvent.onTouchStart(ev);
                  }}
                  onClick={() => setPreset(preset)}
                  data-preset-id={preset.name}
                />
              ))}
          </>
        )}
      </div>
      <div
        className={twCls(
          width('w-full'),
          display('grid'),
          gridTemplateColumns('grid-cols-1'),
          gridTemplateRows('grid-rows-min-content-5'),
        )}
      >
        <div
          className={twCls(
            width('w-full'),
            display('grid'),
            gridAutoFlow('grid-flow-row'),
            gridAutoRows('auto-rows-min'),
            gridTemplateColumns('grid-cols-responsive-buttons'),
          )}
        >
          <NewPresetButton
            className={twCls(justifySelf('justify-self-start'))}
            onClick={createNewPreset}
          >
            New
          </NewPresetButton>
        </div>
        {!isLoading && (
          <div
            className={twCls(
              width('w-full'),
              display('grid'),
              gridTemplateRows('grid-rows-min-content-5'),
            )}
          >
            <Button className={twCls(margin('mt-5'))} onClick={toggleLights}>
              {allLightsOn ? 'All Lights Off' : 'All Lights On'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
